import { ToolPageHeader } from "@/shared/dashboard/workspace/ToolPage";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  Download,
  History,
  Link2,
  LoaderCircle,
  Lock,
  MessageSquare,
  Mic,
  Pencil,
  Plus,
  Target,
  Trash2,
  Unlink,
} from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { privateExerciseFetch } from "@/shared/lib/privateExerciseApi";
import {
  privateExerciseRunPath,
  privateExerciseDebriefPath,
  privateExerciseExportFilename,
  sharedPrivateExercise,
  type PrivateExerciseDifficulty,
  type PrivateExerciseListItem,
  type PrivateExerciseMode,
} from "@/shared/lib/privateExercises";

type Locale = "en" | "uk" | "pl" | "ro";

export const CONSUMER_EXERCISE_COPY = {
  en: {
    eyebrow: "Private practice",
    title: "Your private scenarios",
    body: "Build a role-play around the conversation you need to rehearse. Your private scenarios, transcripts, and AI debriefs stay private to your account.",
    create: "Create scenario",
    emptyTitle: "Create your first private scenario",
    emptyBody:
      "Define the roles, situation, desired outcome, and what the AI should assess.",
    start: "Start scenario",
    edit: "Edit",
    remove: "Delete",
    share: "Share",
    shareTitle: "Share scenario",
    shareHint:
      "Anyone with the private link can read this scenario. Transcripts and debriefs are never shared.",
    createLink: "Create private link",
    copyLink: "Copy link",
    copied: "Copied",
    revokeLink: "Revoke link",
    downloadJson: "Download JSON",
    shareError: "We couldn't update the private link.",
    history: "Past transcripts & debriefs",
    noHistory: "No completed sessions yet",
    private: "Owner only",
    goal: "Goal",
    criteria: "Criteria",
    runs: "Runs",
    best: "Best score",
    loading: "Loading your scenarios…",
    difficulty: { easy: "Easy", medium: "Medium", hard: "Hard" },
    mode: { voice: "Voice", text: "Chat" },
    deleteConfirm:
      "Delete this scenario and its private session history? This cannot be undone.",
    loadError: "We couldn't load your scenarios.",
    retry: "Try again",
  },
  uk: {
    eyebrow: "Приватна практика",
    title: "Ваші приватні сценарії",
    body: "Створіть рольову вправу для потрібної розмови. Вправи, стенограми й AI-розбори доступні лише вам.",
    create: "Створити сценарій",
    emptyTitle: "Створіть перший приватний сценарій",
    emptyBody:
      "Опишіть ролі, ситуацію, бажаний результат і критерії оцінювання AI.",
    start: "Почати сценарій",
    edit: "Редагувати",
    remove: "Видалити",
    share: "Поділитися",
    shareTitle: "Поділитися сценарієм",
    shareHint:
      "Кожен, хто має приватне посилання, зможе прочитати сценарій. Стенограми й розбори не передаються.",
    createLink: "Створити приватне посилання",
    copyLink: "Копіювати посилання",
    copied: "Скопійовано",
    revokeLink: "Відкликати посилання",
    downloadJson: "Завантажити JSON",
    shareError: "Не вдалося оновити приватне посилання.",
    history: "Минулі стенограми й розбори",
    noHistory: "Завершених сесій ще немає",
    private: "Лише власник",
    goal: "Мета",
    criteria: "Критерії",
    runs: "Спроби",
    best: "Найкращий бал",
    loading: "Завантажуємо ваші вправи…",
    difficulty: { easy: "Легка", medium: "Середня", hard: "Складна" },
    mode: { voice: "Голос", text: "Чат" },
    deleteConfirm:
      "Видалити цю вправу та її приватну історію? Це неможливо скасувати.",
    loadError: "Не вдалося завантажити вправи.",
    retry: "Спробувати ще",
  },
  pl: {
    eyebrow: "Prywatna praktyka",
    title: "Twoje prywatne scenariusze",
    body: "Zbuduj scenkę do rozmowy, którą chcesz przećwiczyć. Ćwiczenia, transkrypcje i oceny AI są prywatne.",
    create: "Utwórz scenariusz",
    emptyTitle: "Utwórz pierwsze prywatne ćwiczenie",
    emptyBody: "Określ role, sytuację, wynik i kryteria oceny AI.",
    start: "Rozpocznij ćwiczenie",
    edit: "Edytuj",
    remove: "Usuń",
    share: "Udostępnij",
    shareTitle: "Udostępnij scenariusz",
    shareHint:
      "Każda osoba z prywatnym linkiem może przeczytać scenariusz. Transkrypcje i omówienia nie są udostępniane.",
    createLink: "Utwórz prywatny link",
    copyLink: "Kopiuj link",
    copied: "Skopiowano",
    revokeLink: "Unieważnij link",
    downloadJson: "Pobierz JSON",
    shareError: "Nie udało się zaktualizować prywatnego linku.",
    history: "Poprzednie transkrypcje i omówienia",
    noHistory: "Brak ukończonych sesji",
    private: "Tylko właściciel",
    goal: "Cel",
    criteria: "Kryteria",
    runs: "Podejścia",
    best: "Najlepszy wynik",
    loading: "Wczytujemy twoje ćwiczenia…",
    difficulty: { easy: "Łatwe", medium: "Średnie", hard: "Trudne" },
    mode: { voice: "Głos", text: "Czat" },
    deleteConfirm:
      "Usunąć ćwiczenie i jego prywatną historię? Tej operacji nie można cofnąć.",
    loadError: "Nie udało się wczytać ćwiczeń.",
    retry: "Spróbuj ponownie",
  },
  ro: {
    eyebrow: "Practică privată",
    title: "Scenariile tale private",
    body: "Creează un joc de rol pentru conversația pe care vrei să o repeți. Exercițiile, transcrierile și evaluările AI rămân private.",
    create: "Creează scenariu",
    emptyTitle: "Creează primul exercițiu privat",
    emptyBody:
      "Definește rolurile, situația, rezultatul și criteriile evaluate de AI.",
    start: "Începe exercițiul",
    edit: "Editează",
    remove: "Șterge",
    share: "Distribuie",
    shareTitle: "Distribuie scenariul",
    shareHint:
      "Oricine are linkul privat poate citi scenariul. Transcrierile și evaluările nu sunt distribuite.",
    createLink: "Creează link privat",
    copyLink: "Copiază linkul",
    copied: "Copiat",
    revokeLink: "Revocă linkul",
    downloadJson: "Descarcă JSON",
    shareError: "Linkul privat nu a putut fi actualizat.",
    history: "Transcrieri și evaluări anterioare",
    noHistory: "Nicio sesiune finalizată",
    private: "Doar proprietarul",
    goal: "Obiectiv",
    criteria: "Criterii",
    runs: "Încercări",
    best: "Cel mai bun scor",
    loading: "Se încarcă exercițiile tale…",
    difficulty: { easy: "Ușor", medium: "Mediu", hard: "Dificil" },
    mode: { voice: "Vocal", text: "Chat" },
    deleteConfirm:
      "Ștergi exercițiul și istoricul privat? Acțiunea nu poate fi anulată.",
    loadError: "Exercițiile nu au putut fi încărcate.",
    retry: "Încearcă din nou",
  },
} as const;
// Compatibility export for the legacy dashboard widget adapter.
export const CONSUMER_DASHBOARD_EMPTY_COPY = CONSUMER_EXERCISE_COPY;

type Copy = (typeof CONSUMER_EXERCISE_COPY)[Locale];

const NAVY = "var(--ink-primary)";
const ACCENT = "var(--mark-open-fg)";
const HAIRLINE = "var(--line)";

const BTN_PRIMARY = "eq-button eq-button--primary";
const ICON_BUTTON = "eq-button eq-button--quiet eq-button--icon";

function navigate(path: string) {
  (window as unknown as { navigate: (path: string) => void }).navigate(path);
}

/** A quiet inline stat: useful when scanning, visually subordinate to Start. */
function CompactStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-l border-[var(--line)] pl-3 first:border-l-0 first:pl-0">
      <dt className="ed-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-tertiary)]">
        {label}
      </dt>
      <dd
        className="mt-0.5 truncate text-sm font-semibold"
        style={{ color: NAVY }}
      >
        {value}
      </dd>
    </div>
  );
}

function ExerciseCard({
  exercise,
  copy,
  locale,
  onDelete,
  onUpdated,
}: {
  exercise: PrivateExerciseListItem;
  copy: Copy;
  locale: string;
  onDelete: (exercise: PrivateExerciseListItem) => void;
  onUpdated: (exercise: PrivateExerciseListItem) => void;
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const attempts = exercise.attempts;
  const best =
    attempts.length > 0
      ? Math.max(...attempts.map((attempt) => attempt.overall_score))
      : null;
  const shareUrl = exercise.share_token
    ? `${window.location.origin}/shared/exercise/${exercise.share_token}`
    : null;

  const createShareLink = async () => {
    setShareBusy(true);
    setShareError(null);
    try {
      const { token } = await privateExerciseFetch<{ token: string }>(
        `/api/private-exercises/${encodeURIComponent(exercise.id)}/share`,
        { method: "POST" },
      );
      onUpdated({ ...exercise, share_token: token });
    } catch (error) {
      setShareError(error instanceof Error ? error.message : copy.shareError);
    } finally {
      setShareBusy(false);
    }
  };

  const revokeShareLink = async () => {
    setShareBusy(true);
    setShareError(null);
    try {
      await privateExerciseFetch<void>(
        `/api/private-exercises/${encodeURIComponent(exercise.id)}/share`,
        { method: "DELETE" },
      );
      onUpdated({
        ...exercise,
        share_token: null,
        share_token_created_at: null,
      });
      setCopied(false);
    } catch (error) {
      setShareError(error instanceof Error ? error.message : copy.shareError);
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setShareError(copy.shareError);
    }
  };

  const downloadJson = () => {
    const payload = sharedPrivateExercise(exercise);
    const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = privateExerciseExportFilename(exercise.title);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <article
      className="eq-surface flex flex-col overflow-hidden"
      style={{ borderColor: HAIRLINE }}
    >
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <span
            className="ed-mono inline-flex min-h-7 items-center gap-2 rounded-full bg-[var(--mark-open-bg)] px-3 text-[10px] uppercase tracking-[0.12em]"
            style={{ color: ACCENT }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full bg-[var(--mark-open)]"
              aria-hidden="true"
            />
            {copy.difficulty[exercise.difficulty as PrivateExerciseDifficulty]}
          </span>
          <div className="-my-2 -mr-2 flex shrink-0 gap-0.5">
            <button
              aria-label={`${copy.share}: ${exercise.title}`}
              aria-expanded={shareOpen}
              onClick={() => {
                setShareOpen((open) => !open);
                setShareError(null);
              }}
              className={`${ICON_BUTTON} hover:bg-[#0c1a33]/5 hover:text-[var(--ink-primary)]`}
            >
              <Link2 size={15} aria-hidden="true" />
            </button>
            <button
              aria-label={`${copy.edit}: ${exercise.title}`}
              onClick={() =>
                navigate(`/exercises/${encodeURIComponent(exercise.id)}/edit`)
              }
              className={`${ICON_BUTTON} hover:bg-[#0c1a33]/5 hover:text-[var(--ink-primary)]`}
            >
              <Pencil size={15} aria-hidden="true" />
            </button>
            <button
              aria-label={`${copy.remove}: ${exercise.title}`}
              onClick={() => onDelete(exercise)}
              className={`${ICON_BUTTON} eq-button--danger`}
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </div>
        </div>

        <h2
          className="ed-serif mt-4 text-[1.65rem] leading-[1.15]"
          style={{ color: NAVY }}
        >
          {exercise.title}
        </h2>
        <p className="mt-3 line-clamp-2 text-[0.9375rem] leading-6 text-[var(--ink-tertiary)]">
          {exercise.scenario}
        </p>

        <div className="mt-5 flex items-start gap-3 rounded-[var(--radius-surface)] border border-[var(--line)] bg-[var(--paper-canvas)] p-4">
          <span
            className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-[var(--radius-control)] bg-white text-[var(--mark-open-fg)] shadow-sm"
            aria-hidden="true"
          >
            <Target size={15} />
          </span>
          <div className="min-w-0">
            <p className="ed-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-tertiary)]">
              {copy.goal}
            </p>
            <p className="mt-1 line-clamp-2 text-sm leading-6 text-[var(--ink-secondary)]">
              {exercise.objective}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4 border-t border-[var(--line)] pt-5 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={() => navigate(privateExerciseRunPath(exercise.id))}
            className={`${BTN_PRIMARY} w-full sm:w-auto`}
          >
            {copy.start}
            <ArrowRight size={16} aria-hidden="true" />
          </button>
          <dl className="grid grid-cols-2 gap-4 sm:shrink-0">
            <CompactStat
              label={copy.criteria}
              value={String(exercise.assessment_criteria.length)}
            />
            <CompactStat
              label={copy.best}
              value={best === null ? "—" : `${best}/100`}
            />
          </dl>
        </div>

        {shareOpen && (
          <section
            aria-label={copy.shareTitle}
            className="mt-5 border-t border-[var(--line)] pt-5"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-lg">
                <h3 className="text-sm font-semibold text-[var(--ink-primary)]">
                  {copy.shareTitle}
                </h3>
                <p className="mt-1 text-xs leading-5 text-[var(--ink-tertiary)]">
                  {copy.shareHint}
                </p>
              </div>
              <button
                type="button"
                onClick={downloadJson}
                className="eq-button eq-button--quiet shrink-0"
              >
                <Download size={15} aria-hidden="true" />
                {copy.downloadJson}
              </button>
            </div>

            {shareUrl ? (
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="min-w-0 flex-1 truncate rounded-[var(--radius-control)] border border-[var(--line)] bg-[var(--paper-canvas)] px-3 py-2.5 text-xs text-[var(--ink-secondary)]">
                  {shareUrl}
                </code>
                <button
                  type="button"
                  onClick={() => void copyShareLink()}
                  disabled={shareBusy}
                  className="eq-button"
                >
                  {copied ? (
                    <Check size={15} aria-hidden="true" />
                  ) : (
                    <Copy size={15} aria-hidden="true" />
                  )}
                  {copied ? copy.copied : copy.copyLink}
                </button>
                <button
                  type="button"
                  onClick={() => void revokeShareLink()}
                  disabled={shareBusy}
                  className="eq-button eq-button--quiet eq-button--danger"
                >
                  {shareBusy ? (
                    <LoaderCircle
                      className="animate-spin"
                      size={15}
                      aria-hidden="true"
                    />
                  ) : (
                    <Unlink size={15} aria-hidden="true" />
                  )}
                  {copy.revokeLink}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void createShareLink()}
                disabled={shareBusy}
                className="eq-button mt-4"
              >
                {shareBusy ? (
                  <LoaderCircle
                    className="animate-spin"
                    size={15}
                    aria-hidden="true"
                  />
                ) : (
                  <Link2 size={15} aria-hidden="true" />
                )}
                {copy.createLink}
              </button>
            )}
            {shareError && (
              <p role="alert" className="mt-3 text-xs text-red-700">
                {shareError}
              </p>
            )}
          </section>
        )}
      </div>

      {attempts.length === 0 ? (
        <div className="flex min-h-12 items-center gap-2 border-t border-[var(--line)] bg-[var(--paper-canvas)] px-5 text-xs text-[var(--ink-tertiary)] sm:px-6">
          <History size={13} aria-hidden="true" />
          {copy.noHistory}
        </div>
      ) : (
        <details className="group/history border-t border-[var(--line)] bg-[var(--paper-canvas)]">
          <summary className="flex min-h-14 cursor-pointer list-none items-center gap-2 px-5 text-sm font-medium text-[var(--ink-secondary)] transition-colors duration-200 hover:bg-[var(--paper-sunken)] hover:text-[var(--ink-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-ring)] sm:px-6 [&::-webkit-details-marker]:hidden">
            <History size={14} aria-hidden="true" />
            <span>{copy.history}</span>
            <span className="rounded-full bg-[var(--paper-sunken)] px-2 py-0.5 text-xs font-semibold text-[var(--ink-tertiary)]">
              {attempts.length}
            </span>
            <ChevronDown
              size={15}
              className="ml-auto transition-transform duration-200 group-open/history:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <ul className="max-h-[28rem] overflow-y-auto border-t border-[var(--line)] bg-white px-5 sm:px-6">
            {attempts.map((attempt) => (
              <li key={attempt.id}>
                <button
                  onClick={() =>
                    navigate(
                      privateExerciseDebriefPath(exercise.id, attempt.id),
                    )
                  }
                  className="flex min-h-14 w-full cursor-pointer items-center justify-between gap-3 border-b border-[var(--line)] text-left text-sm text-[var(--ink-secondary)] transition-colors duration-200 last:border-0 hover:text-[var(--mark-open-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-ring)]"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {attempt.mode === "voice" ? (
                      <Mic
                        size={14}
                        className="shrink-0 text-[var(--ink-muted)]"
                        aria-hidden="true"
                      />
                    ) : (
                      <MessageSquare
                        size={14}
                        className="shrink-0 text-[var(--ink-muted)]"
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate">
                      {new Date(
                        attempt.completed_at ?? attempt.created_at,
                      ).toLocaleDateString(locale)}
                      <span className="text-[var(--ink-muted)]">
                        {" "}
                        · {copy.mode[attempt.mode as PrivateExerciseMode]}
                      </span>
                    </span>
                  </span>
                  <span
                    className="flex shrink-0 items-center gap-1.5 font-semibold"
                    style={{ color: NAVY }}
                  >
                    {attempt.overall_score}/100
                    <ArrowRight size={13} aria-hidden="true" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </article>
  );
}

export function PrivateScenariosPage() {
  const { locale } = useLanguage();
  const copy =
    CONSUMER_EXERCISE_COPY[locale as Locale] ?? CONSUMER_EXERCISE_COPY.en;
  const [exercises, setExercises] = useState<PrivateExerciseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await privateExerciseFetch<{
        exercises: PrivateExerciseListItem[];
      }>("/api/private-exercises");
      // Catalog copies live on their catalog cards, not in "my scenarios".
      setExercises(data.exercises.filter((exercise) => !exercise.catalog_slug));
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadError]);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = useCallback(
    async (exercise: PrivateExerciseListItem) => {
      if (!window.confirm(copy.deleteConfirm)) return;
      try {
        await privateExerciseFetch<void>(
          `/api/private-exercises/${encodeURIComponent(exercise.id)}`,
          { method: "DELETE" },
        );
        setExercises((current) =>
          current.filter((item) => item.id !== exercise.id),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : copy.loadError);
      }
    },
    [copy.deleteConfirm, copy.loadError],
  );

  const updateExercise = useCallback((updated: PrivateExerciseListItem) => {
    setExercises((current) =>
      current.map((exercise) =>
        exercise.id === updated.id ? updated : exercise,
      ),
    );
  }, []);

  const cards = useMemo(
    () =>
      exercises.map((exercise) => (
        <ExerciseCard
          key={exercise.id}
          exercise={exercise}
          copy={copy}
          locale={locale}
          onDelete={remove}
          onUpdated={updateExercise}
        />
      )),
    [exercises, copy, locale, remove, updateExercise],
  );

  return (
    <div className="eq-tool-page">
      <section aria-labelledby="consumer-dashboard-heading">
        <ToolPageHeader
          titleId="consumer-dashboard-heading"
          title={copy.title}
          description={copy.body}
          eyebrow={
            <span className="inline-flex items-center gap-2">
              <Lock size={12} aria-hidden="true" />
              {copy.eyebrow} · {copy.private}
            </span>
          }
          actions={
            <button
              onClick={() => navigate("/exercises/new")}
              className={BTN_PRIMARY}
            >
              <Plus size={17} aria-hidden="true" />
              {copy.create}
            </button>
          }
        />

        {error && (
          <div
            role="alert"
            className="mt-6 flex items-center justify-between gap-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            <span>{error}</span>
            <button
              onClick={() => void load()}
              className="min-h-11 cursor-pointer px-3 font-semibold underline"
            >
              {copy.retry}
            </button>
          </div>
        )}

        {loading ? (
          <div
            role="status"
            aria-label={copy.loading}
            className="grid gap-5 xl:grid-cols-2"
          >
            {[0, 1].map((key) => (
              <div
                key={key}
                className="eq-surface eq-panel min-h-80 motion-safe:animate-pulse"
                style={{ borderColor: HAIRLINE }}
              >
                <div className="h-7 w-20 rounded-full bg-[var(--paper-sunken)]" />
                <div className="mt-5 h-7 w-3/4 rounded bg-[var(--paper-sunken)]" />
                <div className="mt-5 space-y-2">
                  <div className="h-3 w-full rounded bg-[var(--paper-sunken)]" />
                  <div className="h-3 w-5/6 rounded bg-[var(--paper-sunken)]" />
                </div>
                <div className="mt-6 h-20 rounded-[var(--radius-surface)] bg-[var(--paper-sunken)]" />
                <div className="mt-6 h-11 w-40 rounded-[var(--radius-control)] bg-[var(--paper-sunken)]" />
              </div>
            ))}
          </div>
        ) : exercises.length === 0 ? (
          <button
            onClick={() => navigate("/exercises/new")}
            className="eq-surface eq-surface--interactive eq-empty-state min-h-72 w-full"
          >
            <span
              className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-surface)] bg-[var(--mark-open-bg)] shadow-sm"
              style={{ color: ACCENT }}
            >
              <Plus size={20} aria-hidden="true" />
            </span>
            <span
              className="ed-serif mt-5 text-[1.65rem]"
              style={{ color: NAVY }}
            >
              {copy.emptyTitle}
            </span>
            <span className="mt-2 max-w-xl text-[0.9375rem] leading-6 text-[var(--ink-tertiary)]">
              {copy.emptyBody}
            </span>
          </button>
        ) : (
          <div className="grid gap-5 xl:grid-cols-2">{cards}</div>
        )}
      </section>
    </div>
  );
}
